/**
 * Groups employees based on association to the organization. The terminated employees are show seperated in a filtered list.
 *
 * For employees the assigned functions are added as related items.
 *
 * @param employees
 */
import type { FunctionViewModel } from '@trinnvis/dabih-api-client';
import { displayName, entityNameForLists } from '../../../utilities/text';
import type { EmployeeViewModelWithName } from '../../types';
import _ from 'lodash';
import type { EmployeeGroup } from '../../../pages/employees-page/employee-group';
import { groupBy } from '../utilities/groupBy';

export function relatedFunctions(employeeId: string, functions: FunctionViewModel[]): string[] {
  const result = functions
    .filter((f) => f.type !== 'COMMON')
    .filter((f) => f.employees.includes(employeeId))
    .map((f) => displayName(f.name));
  return result.sort((a, b) => a.localeCompare(b));
}

/**
 * Creates a grouped list of employees based on the supplied groupKeyValue function. There will always be a seperate group based on
 * the status of the employees. If the employee is TERMINATED they wil always belong to the special group "Sluttet"
 *
 * @param hrefPrefix
 * @param employees
 * @param functionsNotDeleted
 */
export function toGroupedEmployees(
  hrefPrefix: string,
  employees: EmployeeViewModelWithName[],
  functionsNotDeleted: FunctionViewModel[],
  groupKeyValue: (e: EmployeeViewModelWithName) => string,
): EmployeeGroup[] {
  const groupedEmployees = groupBy(employees, (e) =>
    e.status === 'TERMINATED' ? 'TERMINATED' : groupKeyValue(e).toLocaleLowerCase(),
  );
  const result: EmployeeGroup[] = _.sortBy(groupedEmployees, [
    (g) => (g.group === 'TERMINATED' ? 1 : 0),
    (g) => g.group === '',
    (g) => g.group,
  ]).map((g) => {
    let employeeGroupName: string;
    if (g.group === 'TERMINATED') {
      employeeGroupName = 'Sluttet';
    } else {
      employeeGroupName = groupKeyValue(g.items[0]);
    }
    return {
      employeeGroupName,
      terminated: g.group === 'TERMINATED',
      employees: g.items.map((e) => {
        let href = hrefPrefix + 'employees/' + e.uuid;
        if (!e.isConfirmedEntity) {
          href += '?edit';
        }
        return {
          uuid: e.uuid,
          name: entityNameForLists(e.name, e.draftName, e.isConfirmedEntity),
          profession: e.profession ?? '',
          accessControl: [],
          classification: 'NONE',
          locked: false,
          accessible: true,
          href,
          functions: relatedFunctions(e.uuid, functionsNotDeleted),
          hasDraft: e.hasDraft,
        };
      }),
    };
  });
  return result;
}
